import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import LoadingSpinner from '../../../../../../common/components/LoadingSpinner';
import SHRMMessage from '../../../../../../common/components/SHRMMessage';
import { useAppSelector } from '../../../../../../common/hooks';
import { IAddress } from '../../../../../../models/Account/IAddress';
import { IContactConfig } from '../../../../../../models/Account/IContactConfig';
import { IBasicItem } from '../../../../../../models/IBasicItem';
import { RootState } from '../../../../../../redux/store';
import { handleErrorResponse, lookup } from '../../../../../../services';
import AddressDropdown from '../AddressDropdown';
import styles from './AddressModal.module.css';
import { IAddressSuggestion } from '../../../../../../models/Account/IAddressSuggestion';
import { env } from '../../../../../../env';

type AddressModalProps = {
  addressModalClose: () => void;
  onAddEditAddress: (newPhone: IAddress) => Promise<any>;
  existingAddress: IAddress;
  addresses: IAddress[];
  onChangeCountry: (countryCode: string) => Promise<any>;
  fetchStates: (countryCode: string) => Promise<any>;
};

const AddressModal = (props: AddressModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isBilling, setIsBilling] = useState(props.existingAddress?.isBilling);
  const [isShipping, setIsShipping] = useState(props.existingAddress?.isShipping);
  const [popupMesage, setPopupMessage] = useState('');
  const [popupMessageColor, setPopupMessageColor] = useState('');
  const [showPopupMessage, setShowPopupMessage] = useState(false);
  const [searchParam, setSearchParam] = useState<string>('');
  const [suggested, setSuggested] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<IAddressSuggestion[]>([]);
  const [addressVerified, setAddressVerified] = useState<boolean>(true);
  const [noSuggestions, setNoSuggestions] = useState<boolean>(false);
  const [typeMoreForSuggestions, setTypeMoreForSuggestions] = useState<boolean>(false);
  const [addressSelected, setAddressSelected] = useState<boolean>(true);

  const {
    register,
    handleSubmit,
    setValue,
    setFocus,
    formState: { errors, isValid, isDirty },
  } = useForm<IAddress>({
    mode: 'all',
    defaultValues: { ...props.existingAddress },
  });

  const contactConfigFromRedux = useAppSelector(
    (state: RootState) => state.accountReducer.contactConfigData
  );
  const [contactConfigData, setContactConfigData] = useState(contactConfigFromRedux);

  const contactEditData = useAppSelector(
    (state: RootState) => state.accountReducer.contactEditData
  );
  const [states, setStates] = useState(contactEditData.states);

  const [smartStreetsEnabled, setSmartStreetsEnabled] = useState<boolean>(
    env.REACT_APP_SMARTSTREETS_COUNTRY === props.existingAddress?.countryCode
  );

  const changeCountry = (countryCode: string, verify: boolean) => {
    setIsLoading(true);
    Promise.all([props.onChangeCountry(countryCode), props.fetchStates(countryCode)])
      .then(([response1, response2]) => {
        setContactConfigData(response1.data as IContactConfig);
        setStates(response2.data as IBasicItem[]);
        setSmartStreetsEnabled(env.REACT_APP_SMARTSTREETS_COUNTRY === countryCode);
        if (verify) {
          setAddressVerified(smartStreetsEnabled === false);
        }
      })
      .catch((_error: any) => {
        handleErrorResponse(_error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const suggestAddress = param => {
    setAddressSelected(false);
    setNoSuggestions(false);
    setAddressVerified(true);
    if (param && param.length > 3) {
      setTypeMoreForSuggestions(false);
      setSearchParam(param);
      lookup(param).then(response => {
        setSuggested(true);
        setNoSuggestions(response.data.suggestions.length === 0);
        setSuggestions(response.data.suggestions);
      });
    } else if (param && param.length <= 3 && suggested) {
      setSuggestions([]);
      setTypeMoreForSuggestions(true);
    }
  };

  const changeAddressField = () => {
    setSuggestions([]);
    if (addressSelected === false) {
      setAddressVerified(smartStreetsEnabled === false);
    }
  };

  const onChangeOtherFields = () => {
    setAddressSelected(false);
    setAddressVerified(smartStreetsEnabled === false);
  };

  const onSelect = (streetLine, state, city, zipCode) => {
    setValue('addressLine1', streetLine);
    setValue('state', state);
    setValue('city', city);
    setValue('postalCode', zipCode);
    setAddressSelected(true);
    setAddressVerified(true);
  };

  const _handleSubmit = data => {
    const newAddress: IAddress = {} as IAddress;
    newAddress.customerAddressId = data.customerAddressId;
    newAddress.contactEntryType = 1;
    newAddress.contactLocation = 0;
    newAddress.isBilling = data.isBilling;
    newAddress.isShipping = data.isShipping;
    newAddress.addressee = data.addressee;
    newAddress.mailStop = '';
    newAddress.addressLine1 = data.addressLine1;
    newAddress.addressLine2 = data.addressLine2;
    newAddress.postalCode = data.postalCode;
    newAddress.city = data.city;
    newAddress.state = data.state;
    newAddress.countryCode = data.countryCode;
    newAddress.countryDescription = '';
    setIsLoading(true);
    props
      .onAddEditAddress(newAddress)
      .then((_response: any) => {
        props.addressModalClose();
      })
      .catch((_error: any) => {
        const message = handleErrorResponse(_error);
        setPopupMessage(message);
        setPopupMessageColor('var(--shrmRed)');
        setShowPopupMessage(true);
      })
      .finally(() => {
        setIsLoading(false);
        setTimeout(() => {
          setShowPopupMessage(false);
        }, 3000);
      });
  };

  useEffect(() => {
    changeCountry(props.existingAddress.countryCode, false);
  }, []);

  return (
    <div id='modal'>
      <form className={styles.container} onSubmit={handleSubmit(_handleSubmit)}>
        <div
          className={styles.content}
          onClick={e => {
            e.stopPropagation();
          }}
        >
          <div className={styles.header}>
            <label className={styles.title}>Address</label>
            <button className={styles.closeModalButton} onClick={props.addressModalClose}>
              ×
            </button>
          </div>
          {isLoading ? (
            <div>
              <LoadingSpinner />
            </div>
          ) : (
            <div className={styles.form}>
              <SHRMMessage
                color={popupMessageColor}
                text={popupMesage}
                showMessage={showPopupMessage}
              />
              <input
                type='hidden'
                {...register('customerAddressId')}
                defaultValue={props.existingAddress.customerAddressId}
              />
              <div className={styles.inputLabel}>Address Type:</div>
              <div className={styles.locationContainter}>
                <div className={styles.checkboxes}>
                  <span className={styles.shrmCheckbox}>
                    <input
                      type='checkbox'
                      name='isBilling'
                      defaultChecked={isBilling}
                      key={1}
                      {...register('isBilling')}
                    />
                    <label htmlFor='isBilling'>Billing</label>
                  </span>
                  <span className={styles.shrmCheckbox}>
                    <input
                      type='checkbox'
                      name='isShipping'
                      defaultChecked={isShipping}
                      key={2}
                      {...register('isShipping')}
                    />
                    <label htmlFor='isShipping'>Shipping</label>
                  </span>
                </div>
              </div>
              <div className={styles.countryContainer}>
                <AddressDropdown
                  key='Country'
                  label='Country / Region: '
                  propStyle={styles.dropDownProp}
                  items={contactEditData?.countries}
                  placeholderLabel={'Select country'}
                  {...register('countryCode', {
                    onChange: e => changeCountry(e.target.value, true),
                  })}
                  required
                />
              </div>
              <p className={styles.error}>{errors.countryCode && 'Please enter country/region'}</p>

              <div className={styles.inputLabel}>
                <label>Full name:</label>
                <span className={styles.required}>*</span>
              </div>

              <input
                className={clsx(styles.inputField, errors.addressee && styles.errorField)}
                type='text'
                defaultValue={props.existingAddress?.addressee || ''}
                {...register('addressee', {
                  required: true,
                })}
              />
              {errors.addressee && <p className={styles.error}> {errors.addressee.message}</p>}

              {contactConfigData?.addressConfigModel.panelAddressLine1Visible && (
                <React.Fragment>
                  <div className={styles.inputLabel}>
                    <label>{contactConfigData?.addressConfigModel.addressLine1Label}</label> :
                    {contactConfigData?.addressConfigModel.addressLine1Required && (
                      <span className={styles.required}>*</span>
                    )}
                  </div>
                  {smartStreetsEnabled ? (
                    <React.Fragment>
                      <input
                        id='smartstreetenabled'
                        className={clsx(
                          styles.inputField,
                          errors.addressLine1 && styles.errorField
                        )}
                        type='search'
                        defaultValue={props.existingAddress?.addressLine1 || ''}
                        {...register('addressLine1', {
                          required: contactConfigData?.addressConfigModel.addressLine1Required,
                          maxLength: contactConfigData?.addressConfigModel.addressLine1MaxLength,
                          onChange: e => {
                            suggestAddress(e.target.value);
                          },
                          onBlur: e => changeAddressField(),
                        })}
                      />
                      <div className={styles.smartySuggestion}>
                        {searchParam && noSuggestions && (
                          <React.Fragment>No suggestions available for the address</React.Fragment>
                        )}

                        {searchParam && typeMoreForSuggestions && (
                          <React.Fragment>Type more for filtered suggestion</React.Fragment>
                        )}

                        {suggestions?.map((record, index) => {
                          return (
                            <ul
                              key={index}
                              onMouseDown={_e =>
                                onSelect(
                                  record.streetLine,
                                  record.state,
                                  record.city,
                                  record.zipcode
                                )
                              }
                              className={styles.smartyListItems}
                            >
                              {record.streetLine} , {record.state} {record.city}
                            </ul>
                          );
                        })}

                        {addressVerified === false && (
                          <div className={styles.smartyPromptWrapper}>
                            <p className={styles.smartyPromptTitle}>ADDRESS NOT VERIFIED</p>
                            <input
                              type='button'
                              className={styles.smartyPromptButton}
                              onClick={_e => setFocus('addressLine1')}
                              value='Click here to change the address'
                            />
                            <input
                              type='button'
                              className={styles.smartyPromptButton}
                              onClick={_e => setAddressVerified(true)}
                              value='Click here to certify the address is correct'
                            />
                          </div>
                        )}
                      </div>
                    </React.Fragment>
                  ) : (
                    <input
                      className={clsx(styles.inputField, errors.addressLine1 && styles.errorField)}
                      type='text'
                      defaultValue={props.existingAddress?.addressLine1 || ''}
                      {...register('addressLine1', {
                        required: contactConfigData?.addressConfigModel.addressLine1Required,
                        maxLength: contactConfigData?.addressConfigModel.addressLine1MaxLength,
                      })}
                    />
                  )}
                  {errors.addressLine1 && (
                    <p className={styles.error}>{errors.addressLine1.message}</p>
                  )}
                </React.Fragment>
              )}

              {contactConfigData?.addressConfigModel.panelAddressLine2Visible && (
                <React.Fragment>
                  <div className={styles.inputLabel}>
                    <label>{contactConfigData?.addressConfigModel.addressLine2Label}</label> :
                    {contactConfigData?.addressConfigModel.addressLine2Required && (
                      <span className={styles.required}>*</span>
                    )}
                  </div>
                  <input
                    className={clsx(styles.inputField, errors.addressLine2 && styles.errorField)}
                    type='text'
                    defaultValue={props.existingAddress?.addressLine2 || ''}
                    {...register('addressLine2', {
                      required: contactConfigData?.addressConfigModel.addressLine2Required,
                      maxLength: contactConfigData?.addressConfigModel.addressLine2MaxLength,
                    })}
                  />
                  {errors.addressLine2 && (
                    <p className={styles.error}>{errors.addressLine2.message}</p>
                  )}
                </React.Fragment>
              )}
              <div className={styles.zipGroup}>
                {contactConfigData?.addressConfigModel.panelCityVisible && (
                  <div>
                    <div className={styles.inputLabel}>
                      <label>{contactConfigData?.addressConfigModel.cityLabel}</label> :
                      {contactConfigData?.addressConfigModel.cityRequired && (
                        <span className={styles.required}>*</span>
                      )}
                    </div>
                    <input
                      className={clsx(styles.inputField, errors.city && styles.errorField)}
                      type='text'
                      defaultValue={props.existingAddress?.city || ''}
                      {...register('city', {
                        required: contactConfigData?.addressConfigModel.cityRequired,
                        maxLength: contactConfigData?.addressConfigModel.cityMaxLength,
                        onChange: e => onChangeOtherFields(),
                      })}
                    />
                    {errors.city && <p className={styles.error}>{errors.city.message}</p>}
                  </div>
                )}

                {states?.length > 0 ? (
                  <div>
                    <AddressDropdown
                      key='State'
                      className={errors.state && styles.errorField}
                      label={contactConfigData?.addressConfigModel.stateLabel}
                      propStyle={styles.dropDownProp}
                      items={states}
                      placeholderLabel={'Select state'}
                      onChange={e => onChangeOtherFields()}
                      {...register('state', {
                        onChange: e => onChangeOtherFields(),
                      })}
                      required={contactConfigData?.addressConfigModel.stateRequired}
                    />
                    {errors.state && <p className={styles.error}>{errors.state.message}</p>}
                  </div>
                ) : (
                  <div>
                    <div className={styles.inputLabel}>
                      <label>{contactConfigData?.addressConfigModel.stateLabel}</label> :{' '}
                      {contactConfigData?.addressConfigModel.stateRequired && (
                        <span className={styles.required}>*</span>
                      )}
                    </div>
                    <input
                      className={clsx(styles.inputField, errors.state && styles.errorField)}
                      type='text'
                      defaultValue={props.existingAddress?.state || ''}
                      {...register('state', {
                        required: contactConfigData?.addressConfigModel.stateRequired,
                        maxLength: contactConfigData?.addressConfigModel.stateAsTextMaxLength,
                        onChange: e => onChangeOtherFields(),
                      })}
                    />
                    {errors.state && <p className={styles.error}>{errors.state.message}</p>}
                  </div>
                )}
                {contactConfigData?.addressConfigModel.panelPostCodeVisible && (
                  <div>
                    <div className={styles.inputLabel}>
                      <label>{contactConfigData?.addressConfigModel.postalCodeLabel}</label> :
                      {contactConfigData?.addressConfigModel.postalCodeRequired && (
                        <span className={styles.required}>*</span>
                      )}
                    </div>
                    <input
                      className={clsx(styles.inputField, errors.postalCode && styles.errorField)}
                      type='text'
                      defaultValue={props.existingAddress?.postalCode || ''}
                      {...register('postalCode', {
                        required: contactConfigData?.addressConfigModel.postalCodeRequired,
                        maxLength: contactConfigData?.addressConfigModel.postalCodeMaxLength,
                        onChange: e => onChangeOtherFields(),
                      })}
                    />
                    {errors.postalCode && (
                      <p className={styles.error}>{errors.postalCode.message}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className={styles.footer}>
            <button
              type='submit'
              className={styles.saveButton}
              disabled={!(isDirty && isValid && addressVerified)}
            >
              Save
            </button>
            <div className={styles.cancelButton} onClick={props.addressModalClose}>
              Cancel
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddressModal;
